import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { PROGRAM, getSectionById } from '../data/newCurriculum';
import { getLessonContent } from '../data/allLessonContents';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, Check, Volume2, Mic, RotateCcw, BookOpen, HelpCircle, Award } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Flashcard Component
const FlashcardLesson = ({ lesson, onComplete, unitId, lessonId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [cards, setCards] = useState([]);
  const [learned, setLearned] = useState([]);

  useEffect(() => {
    // Gerçek içerik dosyasından kartları al
    const content = getLessonContent(unitId, lessonId);
    if (content && content.cards) {
      setCards(content.cards);
    } else {
      // Fallback
      setCards([
        { front: 'Hello', back: 'Merhaba' },
        { front: 'Goodbye', back: 'Hoşça kal' },
        { front: 'Thank you', back: 'Teşekkür ederim' },
      ]);
    }
  }, [unitId, lessonId]);

  const handleNext = () => {
    setFlipped(false);
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onComplete();
    }
  };

  const handleMarkLearned = () => {
    setLearned(prev => [...prev, currentIndex]);
    handleNext();
  };

  if (cards.length === 0) {
    return <div className="text-center py-8">Kartlar yükleniyor...</div>;
  }

  const progress = ((currentIndex + 1) / cards.length) * 100;

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center gap-4">
        <Progress value={progress} className="flex-1 h-2" />
        <span className="text-sm text-gray-500">
          {currentIndex + 1} / {cards.length}
        </span>
      </div>

      {/* Flashcard */}
      <div
        className={`min-h-[300px] rounded-2xl cursor-pointer transition-all duration-500 transform perspective-1000 ${
          flipped ? 'rotate-y-180' : ''
        }`}
        onClick={() => setFlipped(!flipped)}
        style={{ transformStyle: 'preserve-3d' }}
      >
        <Card className={`h-full flex items-center justify-center p-8 ${
          flipped ? 'bg-gradient-to-br from-green-50 to-emerald-100' : 'bg-gradient-to-br from-blue-50 to-indigo-100'
        }`}>
          <CardContent className="text-center">
            <p className="text-xs text-gray-500 mb-2">
              {flipped ? 'TÜRKÇE' : 'İNGİLİZCE'}
            </p>
            <h2 className="text-3xl font-bold text-gray-800">
              {flipped ? cards[currentIndex]?.back : cards[currentIndex]?.front}
            </h2>
            <p className="text-sm text-gray-400 mt-4">
              Çevirmek için tıklayın
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => {
            setFlipped(false);
            setCurrentIndex(prev => Math.max(0, prev - 1));
          }}
          disabled={currentIndex === 0}
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Önceki
        </Button>
        <Button
          className="flex-1 bg-green-600 hover:bg-green-700"
          onClick={handleMarkLearned}
        >
          <Check className="h-4 w-4 mr-2" />
          Öğrendim
        </Button>
        <Button
          className="flex-1"
          onClick={handleNext}
        >
          Sonraki
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Quiz Component
const QuizLesson = ({ lesson, onComplete, unitId, lessonId }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    // Gerçek içerik dosyasından soruları al
    const content = getLessonContent(unitId, lessonId);
    if (content && content.questions) {
      setQuestions(content.questions);
    } else {
      // Fallback
      const mockQuestions = [
        {
          question: '"Hello" kelimesinin Türkçe karşılığı nedir?',
          options: ['Güle güle', 'Merhaba', 'Teşekkürler', 'Evet'],
          correct: 1
        },
        {
          question: '"Thank you" ne demek?',
          options: ['Lütfen', 'Hayır', 'Teşekkür ederim', 'Merhaba'],
          correct: 2
        },
      ];
      setQuestions(mockQuestions);
    }
  }, [unitId, lessonId]);

  const handleAnswer = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
    setShowResult(true);
    if (index === questions[currentQuestion].correct) {
      setScore(prev => prev + 1);
    }
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      onComplete();
    }
  };

  if (questions.length === 0) {
    return <div className="text-center py-8">Sorular yükleniyor...</div>;
  }

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const current = questions[currentQuestion];

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center gap-4">
        <Progress value={progress} className="flex-1 h-2" />
        <span className="text-sm text-gray-500">
          Soru {currentQuestion + 1} / {questions.length}
        </span>
      </div>

      {/* Question */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-blue-600 mb-4">
            <HelpCircle className="h-5 w-5" />
            <span className="text-sm font-medium">Soru {currentQuestion + 1}</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            {current.question}
          </h2>
          
          <div className="space-y-3">
            {current.options.map((option, index) => (
              <button
                key={index}
                className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                  selectedAnswer === index
                    ? showResult
                      ? index === current.correct
                        ? 'border-green-500 bg-green-50'
                        : 'border-red-500 bg-red-50'
                      : 'border-blue-500 bg-blue-50'
                    : showResult && index === current.correct
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                }`}
                onClick={() => handleAnswer(index)}
                disabled={showResult}
              >
                <span className="font-medium">{option}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Result & Next */}
      {showResult && (
        <div className="flex items-center justify-between">
          <div className={`px-4 py-2 rounded-lg ${
            selectedAnswer === current.correct
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {selectedAnswer === current.correct
              ? '✅ Doğru!'
              : '❌ Yanlış. Doğru cevap: ' + current.options[current.correct]
            }
          </div>
          <Button onClick={handleNext}>
            {currentQuestion < questions.length - 1 ? 'Sonraki Soru' : 'Tamamla'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
};

// Voice Practice Component
const VoiceLesson = ({ lesson, onComplete, unitId, lessonId }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const [phrases, setPhrases] = useState([]);

  useEffect(() => {
    // Gerçek içerik dosyasından cümleleri al
    const content = getLessonContent(unitId, lessonId);
    if (content && content.phrases) {
      setPhrases(content.phrases);
    } else {
      // Fallback
      setPhrases([
        { text: 'Hello, how are you?', translation: 'Merhaba, nasılsınız?' },
        { text: 'Nice to meet you.', translation: 'Tanıştığımıza memnun oldum.' },
        { text: 'Thank you very much.', translation: 'Çok teşekkür ederim.' },
      ]);
    }
  }, [unitId, lessonId]);

  const handleNext = () => {
    if (currentPhrase < phrases.length - 1) {
      setCurrentPhrase(prev => prev + 1);
    } else {
      onComplete();
    }
  };

  const progress = ((currentPhrase + 1) / phrases.length) * 100;

  if (phrases.length === 0) {
    return <div className="text-center py-8">Cümleler yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center gap-4">
        <Progress value={progress} className="flex-1 h-2" />
        <span className="text-sm text-gray-500">
          {currentPhrase + 1} / {phrases.length}
        </span>
      </div>

      {/* Phrase Card */}
      <Card>
        <CardContent className="p-8 text-center">
          <Button variant="ghost" size="lg" className="mb-4 text-blue-600">
            <Volume2 className="h-8 w-8" />
          </Button>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {phrases[currentPhrase]?.text}
          </h2>
          <p className="text-gray-500">
            {phrases[currentPhrase]?.translation}
          </p>
        </CardContent>
      </Card>

      {/* Record Button */}
      <div className="flex flex-col items-center gap-4">
        <Button
          size="lg"
          className={`w-20 h-20 rounded-full ${
            isRecording ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'bg-blue-600 hover:bg-blue-700'
          }`}
          onClick={() => setIsRecording(!isRecording)}
        >
          <Mic className="h-8 w-8" />
        </Button>
        <p className="text-sm text-gray-500">
          {isRecording ? 'Konuşuyor...' : 'Kayıt için tıklayın'}
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => setCurrentPhrase(prev => Math.max(0, prev - 1))}
          disabled={currentPhrase === 0}
        >
          Önceki
        </Button>
        <Button className="flex-1" onClick={handleNext}>
          {currentPhrase < phrases.length - 1 ? 'Sonraki' : 'Tamamla'}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Roleplay Component
const RoleplayLesson = ({ lesson, onComplete, unitId, lessonId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dialogue, setDialogue] = useState([]);

  useEffect(() => {
    // Gerçek içerik dosyasından diyalogları al
    const content = getLessonContent(unitId, lessonId);
    if (content && content.dialogue) {
      setDialogue(content.dialogue);
    } else {
      // Fallback
      setDialogue([
        { speaker: 'A', text: 'Hello! Welcome aboard.', translation: 'Merhaba! Uçağımıza hoş geldiniz.' },
        { speaker: 'B', text: 'Thank you. Where is my seat?', translation: 'Teşekkürler. Koltuğum nerede?' },
        { speaker: 'A', text: 'Your seat is 15A, by the window.', translation: 'Koltuğunuz 15A, pencere kenarında.' },
        { speaker: 'B', text: 'Great, thank you!', translation: 'Harika, teşekkürler!' },
      ]);
    }
  }, [unitId, lessonId]);

  const handleNext = () => {
    if (currentStep < dialogue.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onComplete();
    }
  };

  if (dialogue.length === 0) {
    return <div className="text-center py-8">Diyalog yükleniyor...</div>;
  }

  const progress = ((currentStep + 1) / dialogue.length) * 100;

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center gap-4">
        <Progress value={progress} className="flex-1 h-2" />
        <span className="text-sm text-gray-500">
          {currentStep + 1} / {dialogue.length}
        </span>
      </div>

      {/* Dialogue */}
      <div className="space-y-4">
        {dialogue.slice(0, currentStep + 1).map((line, index) => (
          <Card key={index} className={`${
            line.speaker === 'A' || line.speaker === 'Interviewer' || line.speaker === 'Assessor' ? 'ml-0 mr-8' : 'ml-8 mr-0'
          }`}>
            <CardContent className={`p-4 ${
              line.speaker === 'A' || line.speaker === 'Interviewer' || line.speaker === 'Assessor' ? 'bg-blue-50' : 'bg-green-50'
            }`}>
              <p className="text-xs text-gray-500 mb-1">
                {line.speaker === 'A' ? '👨‍✈️ Kabin Memuru' : 
                 line.speaker === 'Interviewer' ? '👔 Mülakatçı' :
                 line.speaker === 'Assessor' ? '📋 Değerlendirici' :
                 line.speaker === 'Candidate' ? '🙋 Aday' :
                 '👤 Yolcu'}
              </p>
              <p className="font-medium text-gray-800">{line.text}</p>
              <p className="text-sm text-gray-500 mt-1">{line.translation}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
          disabled={currentStep === 0}
        >
          Önceki
        </Button>
        <Button className="flex-1" onClick={handleNext}>
          {currentStep < dialogue.length - 1 ? 'Sonraki' : 'Tamamla'}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Exam Component
const ExamLesson = ({ lesson, onComplete }) => {
  return (
    <div className="text-center py-12">
      <Award className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Final Sınavı</h2>
      <p className="text-gray-600 mb-6">
        Bu bölümün final sınavına hoş geldiniz. Hazır olduğunuzda başlayın.
      </p>
      <Button size="lg" onClick={onComplete}>
        Sınava Başla
      </Button>
    </div>
  );
};

export default function ProgramLesson() {
  const { sectionId, unitId, lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [completed, setCompleted] = useState(false);
  const [xpResult, setXpResult] = useState(null);

  // Get lesson data from location state or find it
  const section = getSectionById(sectionId);
  const unit = section?.units.find(u => u.id === parseInt(unitId));
  const lesson = unit?.lessons.find(l => l.id === parseInt(lessonId));

  const handleComplete = async () => {
    try {
      // Save lesson progress
      await axios.post(`${API}/program/complete-lesson`, {
        sectionId,
        unitId: parseInt(unitId),
        lessonId: parseInt(lessonId)
      }, { withCredentials: true });
      
      // Add Mil
      const xpAmount = lesson?.type === 'quiz' ? 15 : lesson?.type === 'exam' ? 25 : 10;
      const xpRes = await axios.post(`${API}/gamification/add-xp`, {
        xp: xpAmount,
        minutes: lesson?.duration || 5
      }, { withCredentials: true });
      
      setXpResult(xpRes.data);
      setCompleted(true);
    } catch (error) {
      console.error('Ders tamamlanamadı:', error);
      // Still show completion UI
      setCompleted(true);
    }
  };

  const handleNextLesson = () => {
    // Find next lesson
    const lessonIndex = unit.lessons.findIndex(l => l.id === parseInt(lessonId));
    if (lessonIndex < unit.lessons.length - 1) {
      // Next lesson in same unit
      const nextLesson = unit.lessons[lessonIndex + 1];
      navigate(`/program/${sectionId}/lesson/${unitId}/${nextLesson.id}`);
      setCompleted(false);
    } else {
      // Go back to section page
      navigate(`/program/${sectionId}`);
    }
  };

  if (!section || !unit || !lesson) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Ders bulunamadı</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Ana Sayfaya Dön
          </Button>
        </div>
      </div>
    );
  }

  const renderLessonContent = () => {
    const contentProps = { 
      lesson, 
      onComplete: handleComplete, 
      unitId: parseInt(unitId), 
      lessonId: parseInt(lessonId) 
    };
    
    switch (lesson.type) {
      case 'flashcard':
        return <FlashcardLesson {...contentProps} />;
      case 'quiz':
        return <QuizLesson {...contentProps} />;
      case 'voice':
        return <VoiceLesson {...contentProps} />;
      case 'roleplay':
        return <RoleplayLesson {...contentProps} />;
      case 'exam':
        return <ExamLesson {...contentProps} />;
      default:
        return (
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Bu ders türü henüz hazır değil.</p>
            <Button onClick={handleComplete} className="mt-4">
              Tamamlandı Olarak İşaretle
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/program/${sectionId}`)}
            className="mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {section.title}
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-2xl">{unit.icon}</span>
            <div>
              <p className="text-xs text-gray-500">Ünite {unit.id} • Ders {lesson.id}</p>
              <h1 className="font-bold text-gray-800">{lesson.title}</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {completed ? (
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                <Check className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-800 mb-2">Tebrikler! 🎉</h2>
              <p className="text-green-600 mb-4">Bu dersi başarıyla tamamladınız.</p>
              
              {/* Mil Result */}
              {xpResult && (
                <div className="mb-6 space-y-2">
                  <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full">
                    <span className="text-xl">⚡</span>
                    <span className="font-bold">+{xpResult.miles_earned} Mil</span>
                  </div>
                  {xpResult.new_badges?.length > 0 && (
                    <div className="flex justify-center gap-2">
                      {xpResult.new_badges.map((badge, i) => (
                        <span key={i} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
                          🏆 Yeni Rozet!
                        </span>
                      ))}
                    </div>
                  )}
                  {xpResult.daily_goal_complete && (
                    <div className="text-emerald-600 font-medium">
                      ✅ Günlük hedef tamamlandı!
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate(`/program/${sectionId}`)}>
                  Bölüme Dön
                </Button>
                <Button onClick={handleNextLesson} className="bg-green-600 hover:bg-green-700">
                  Sonraki Ders
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          renderLessonContent()
        )}
      </main>
    </div>
  );
}
